/**
 * Pure formatters for the Lifecycle audit drawer.
 *
 * The drawer renders rows of the form::
 *
 *   ⟿  ACTIVE → NOTIFIED       (sweeper • 73 days inactive)
 *      72 minutes ago by SYSTEM
 *
 * All the "boring" string assembly happens here so the React component
 * is just a list + map and the rules are unit-testable without booting
 * a vitest jsdom environment with a full theme tree.
 *
 * Mirror of the backend audit row schema in
 * ``rest_handlers._lifecycle_event_to_api`` — keep the field list in
 * sync if either side adds a new key.
 */

const STATUS_LABELS = Object.freeze({
  ACTIVE: 'Active',
  NOTIFIED: 'Notified',
  PAUSED: 'Paused',
  RETIRED: 'Retired',
  ARCHIVED: 'Archived',
});

const STATUS_COLORS = Object.freeze({
  ACTIVE: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  NOTIFIED: 'text-red-700 bg-red-50 border-red-200',
  PAUSED: 'text-amber-700 bg-amber-50 border-amber-200',
  RETIRED: 'text-gray-700 bg-gray-100 border-gray-200',
  ARCHIVED: 'text-gray-500 bg-gray-50 border-gray-200',
});

const SYSTEM_REASON_LABELS = Object.freeze({
  'auto-demote-on-activity': 'Demoted by activity',
  'sweeper-notify': 'Auto-notify (inactivity)',
  'sweeper-retire': 'Auto-retire (inactivity)',
  'sweeper-archive': 'Auto-archive',
  MANUAL_ADMIN: 'Workspace admin',
  MANUAL_SUPERVISOR: 'Pool supervisor',
});

export function lifecycleStatusLabel(status) {
  return STATUS_LABELS[String(status || '').toUpperCase()] || String(status || '');
}

export function lifecycleStatusBadgeClass(status) {
  return (
    STATUS_COLORS[String(status || '').toUpperCase()]
    || 'text-gray-700 bg-gray-100 border-gray-200'
  );
}

/** "auto-demote-on-activity" → "Demoted by activity"; passes through unknown reasons. */
export function lifecycleReasonLabel(reason) {
  if (!reason) return '';
  const k = String(reason).trim();
  return SYSTEM_REASON_LABELS[k] || k;
}

/** "USER"|"SYSTEM"|"" → "person"|"sweeper"|"system"; pure for icon mapping. */
export function lifecycleActorRole(actorKind) {
  const k = String(actorKind || '').toUpperCase();
  if (k === 'USER') return 'person';
  if (k === 'SYSTEM') return 'sweeper';
  return 'system';
}

/**
 * "1d ago", "2h ago", "Apr 26 18:42" — relative for the last 24h,
 * absolute (locale-formatted) past that. Falls back to the raw string
 * if it can't parse (corrupt audit row shouldn't crash the drawer).
 *
 * The reference clock is injected so tests are deterministic; in
 * production callers pass ``Date.now()``.
 */
export function lifecycleTimeAgo(occurredAt, nowMs) {
  if (!occurredAt) return '';
  const t = Date.parse(occurredAt);
  if (!Number.isFinite(t)) return String(occurredAt);
  const ref = Number.isFinite(nowMs) ? nowMs : Date.now();
  const deltaSec = Math.max(0, Math.round((ref - t) / 1000));
  if (deltaSec < 45) return 'just now';
  if (deltaSec < 90) return '1 minute ago';
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} minutes ago`;
  if (deltaMin < 90) return '1 hour ago';
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr} hours ago`;
  const deltaDay = Math.round(deltaHr / 24);
  if (deltaDay < 7) return `${deltaDay} day${deltaDay === 1 ? '' : 's'} ago`;
  // Past one week, fall through to a compact absolute form so users
  // don't read "31 days ago" and have to do mental math.
  try {
    const d = new Date(t);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(occurredAt);
  }
}

/**
 * Truncate an actor sub for compact display while keeping enough
 * suffix for an audit reviewer to recognize one user across rows.
 */
export function lifecycleActorLabel(actor, actorKind) {
  const a = String(actor || '').trim();
  if (!a) return 'system';
  if (lifecycleActorRole(actorKind) !== 'person') {
    if (a.toUpperCase() === 'SYSTEM') return 'sweeper';
    return a;
  }
  if (a.length <= 12) return a;
  return `…${a.slice(-8)}`;
}

/**
 * One-stop projection for a single audit row → everything the React
 * component needs. Encapsulates *all* the conditional logic so the
 * JSX stays declarative.
 *
 * @param {object} evt - shape from the backend ``events[]`` entry.
 * @param {number} [nowMs] - reference clock for the relative time.
 */
export function projectLifecycleAuditRow(evt, nowMs) {
  if (!evt || typeof evt !== 'object') {
    return null;
  }
  const toStatus = String(evt.toLifecycleStatus || '').toUpperCase();
  const fromStatus = evt.fromLifecycleStatus
    ? String(evt.fromLifecycleStatus).toUpperCase()
    : '';
  const role = lifecycleActorRole(evt.actorKind);
  const transitionLabel = fromStatus
    ? `${lifecycleStatusLabel(fromStatus)} → ${lifecycleStatusLabel(toStatus)}`
    : lifecycleStatusLabel(toStatus);

  const meta = (evt.metadata && typeof evt.metadata === 'object') ? evt.metadata : null;
  const detailParts = [];
  if (evt.reason) detailParts.push(lifecycleReasonLabel(evt.reason));
  if (meta && Number.isFinite(meta.daysInactive)) {
    detailParts.push(`${meta.daysInactive} days inactive`);
  }
  if (meta && meta.scheduledFor) {
    detailParts.push(`retiring ${meta.scheduledFor}`);
  }
  if (meta && meta.source) {
    detailParts.push(`source: ${meta.source}`);
  }

  return {
    transitionLabel,
    fromStatus,
    toStatus,
    badgeClass: lifecycleStatusBadgeClass(toStatus),
    actorRole: role,
    actorLabel: lifecycleActorLabel(evt.actor, evt.actorKind),
    timeAgo: lifecycleTimeAgo(evt.occurredAt, nowMs),
    detail: detailParts.join(' • '),
    raw: evt,
  };
}

export const __test_only_helpers = {
  STATUS_LABELS,
  STATUS_COLORS,
  SYSTEM_REASON_LABELS,
};
